<template>
  <div class="view-block">
    <h1>Seleccionar placa</h1>
    <panZoom class="design"
             :options="panzoomOptions"
             @init="onPanzoomInit"
             @panend="panend = new Date()"
             ref="panzoomWrapper"
             v-if="ready">
      <div class="submeshes" ref="panzoomedRegion">
        <div v-for="row in blockSizeY"
             class="submesh-row"
             :key="row">
          <div v-for="col in blockSizeX"
               :key="col"
               class="submesh"
               :class="{ 'submesh-empty': isEmptySubmesh(getSubmeshIdx(row - 1, col - 1)), finished: finishedSubmeshes[getSubmeshIdx(row - 1, col - 1)] }"
               :style="overlayStyles[(row - 1) * blockSizeX + col - 1]"
               @touchstart="onTouchstart($event, getSubmeshIdx(row - 1, col - 1))"
               @click="viewSubmesh(getSubmeshIdx(row - 1, col - 1))"
               @touchend="onTouchend($event, getSubmeshIdx(row - 1, col - 1))"
               >
            <div class="overlay-text-container">
              <span class="overlay-text">{{ getSubmeshLabel(row - 1, col - 1) }}</span>
            </div>
          </div>
        </div>
      </div>
    </panZoom>
    <v-btn icon fixed bottom left @click="fitZoom()">
      <v-icon>mdi-magnify-scan</v-icon>
    </v-btn>
    <v-btn icon fixed bottom right @click="showInfo++">
      <v-icon>mdi-information</v-icon>
    </v-btn>
    <v-btn icon fixed bottom right class="btn-bill-of-materials" @click="showBillOfMaterials++">
      <v-icon>mdi-clipboard</v-icon>
    </v-btn>
    <design-info
      v-if="ready"
      v-model="showInfo"
    />
    <bill-of-materials
        v-if="ready"
        v-model="showBillOfMaterials"
      />
    <v-snackbar v-model="showErrorMessage">
      {{errorMessage}}

      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showError(false)"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
    <v-overlay :value="!ready">
      <v-progress-circular indeterminate
                           :size="64"
                           :width="7"></v-progress-circular>
      <div>Cargando bloque...</div>
    </v-overlay>
  </div>
</template>
<script>
import designService from '../services/design.service';
import BillOfMaterials from '../components/BillOfMaterials.vue';
import DesignInfo from '../components/DesignInfo.vue';
import { debounce } from 'lodash';

const RESIZE_DEBOUNCE_MS = 250;
let resizeTimeout = null;
const CLICK_DELTA = 6;

export default {
  name: 'view-block',
  components: {
    'bill-of-materials': BillOfMaterials,
    'design-info': DesignInfo,
  },
  data() {
    return {
      design: null,
      block: null,
      blockIdx: null,
      blockSizeX: 0,
      blockSizeY: 0,
      submeshImages: null,
      overlayStyles: [],
      panzoomInstance: null,
      panzoomOptions: {
        minZoom: 0.01,
        maxZoom: 3,
        autocenter: true,
        bounds: true,
        smoothScroll: false,
      },
      panend: null,
      showInfo: false,
      showBillOfMaterials: false,
      billOfMaterials: null,
      infoValues: [],
      ready: false,
      showErrorMessage: false,
      errorMessage: null,
      touchstartInfo: null,
      finishedSubmeshes: designService.getFinishedSubmeshes(),
    };
  },
  methods: {
    initialize() {
      const blockIdx = this.$route.params.blockIdx || 0;
      if (this.design === designService.getCurrentDesign() && this.blockIdx === blockIdx) {
        return Promise.resolve("false");
      }

      this.ready = false;

      this.design = designService.getCurrentDesign();
      
      this.billOfMaterials = designService.getBillOfMaterials();

      return designService.loadBlock(blockIdx).then((block) => {
        this.submeshImages = block.submeshImages;
        
        this.blockSizeX = Math.min(block.sizeX, this.design.metadata.hsubmeshes - block.startSubmeshX);
        this.blockSizeY = Math.min(block.sizeY, this.design.metadata.vsubmeshes - block.startSubmeshY);

        let imageWidth = Math.floor(95 / this.blockSizeX);
        
        const maxTextSize = Math.max(3,
          block.submeshes.filter(s => s && s.label).map(s => s.label.length).reduce((a, b) => Math.max(a, b)));

        this.overlayStyles = [];
        for (const img of this.submeshImages) {
          this.overlayStyles.push({
            width: `${imageWidth}%`,
            height: 0,
            paddingBottom: `${img.height / img.width * imageWidth}%`,
            fontSize: `${img.height / img.width * imageWidth / 2.5 * 3 / maxTextSize}vw`,
            backgroundImage: `url(${img.url})`,
            boxSizing: 'border-box',
          });
        }

        this.blockIdx = blockIdx;
        this.block = block;
        this.ready = true;
        return true;
      }).catch(e => {
        console.log(`Error loading block: ${e}`);
      });
    },
    getSubmeshIdx(row, col) {
      return (row + this.block.startSubmeshY) * this.design.metadata.hsubmeshes + col + this.block.startSubmeshX;
    },
    getSubmeshLabel(row, col) {
      const submeshIdxInBlock = row * this.blockSizeX + col;
      if (this.block.submeshes[submeshIdxInBlock] && this.block.submeshes[submeshIdxInBlock].label) {
        return this.block.submeshes[submeshIdxInBlock].label;
      } else {
        return "";
      }
    },
    viewSubmesh: debounce(function(idx) {
        if (this.panend && new Date() - this.panend < 60) {
          return;
        }
        if (this.isEmptySubmesh(idx)) {
          this.showError(`La placa está vacía`);
          return;
        }
        this.showError(false);
        this.$router.push({ name: 'view-submesh', params: { id: idx } });
      }, 200),
    isEmptySubmesh(idx) {
      return this.block && this.block.emptySubmeshes && this.block.emptySubmeshes.indexOf(idx) !== -1;
    },
    onPanzoomInit(instance) {
      this.panzoomInstance = instance;
      this.fitZoom();
    },
    onResize() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(this.fitZoom, RESIZE_DEBOUNCE_MS);
    },
    fitZoom() {
      if (!this.panzoomInstance || !this.$refs.panzoomedRegion) {
        return;
      }
      const width = this.$refs.panzoomedRegion.clientWidth + 5,
              height = this.$refs.panzoomedRegion.clientHeight + 5,
              viewportWidth = this.$refs.panzoomWrapper.$el.clientWidth,
              viewportHeight = this.$refs.panzoomWrapper.$el.clientHeight,
              minScale = Math.min(viewportWidth / width, viewportHeight / height);
      this.panzoomInstance.pause();
      this.panzoomInstance.setMinZoom(minScale);
      this.panzoomInstance.zoomAbs(0, 0, minScale);
      this.panzoomInstance.moveTo((viewportWidth - width * minScale) / 2, (viewportHeight - height * minScale) / 2);
      this.panzoomInstance.resume();
    },
    showError(msg) {
      if (msg) {
        this.errorMessage = msg;
        this.showErrorMessage = true;
      } else {
        this.errorMessage = null;
        this.showErrorMessage = false;
      }
    },
    onTouchstart(event, submeshIdx) {
      if (event.touches.length > 1) {
        this.touchstartInfo = null;
      } else {
        this.touchstartInfo = {
          submeshIdx,
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
      }
    },
    onTouchend(event, submeshIdx) {
      if (event.touches.length > 0) {
        return;
      }
      if (!this.touchstartInfo) {
        return;
      }
      if (this.touchstartInfo.submeshIdx === submeshIdx
              && Math.abs(event.changedTouches[0].clientX - this.touchstartInfo.x) <= CLICK_DELTA
              && Math.abs(event.changedTouches[0].clientY - this.touchstartInfo.y) <= CLICK_DELTA) {
        this.viewSubmesh(submeshIdx);
      }
    },
  },
  created() {
    window.addEventListener("resize", this.onResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
  },
  activated() {
    this.showInfo = false;
    this.showBillOfMaterials = false;
    this.initialize().then((v) => v && this.fitZoom()).finally(() => {
      this.finishedSubmeshes = designService.getFinishedSubmeshes();
    });
  }
};
</script>
<style>
.v-main {
  height: 100%;
}
.view-block {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.design {
  flex: 1;
  overflow: hidden;
}
.submesh-row {
  display: flex;
  flex-flow: wrap;
  justify-content: center;
  position: relative;
}
.submeshes .submesh {
  margin: 0;
  padding: 0;
  border: 2px solid black;
  margin-right: -2px;
  margin-bottom: -2px;
  flex: none;
  position: relative;
  text-align: center;
  text-shadow: 2px 2px 15px #ffffff;
  font-weight: bold;
  background: none transparent center no-repeat;
  background-size: contain;
  cursor: pointer;
}
.submeshes .submesh.submesh-empty {
  cursor: not-allowed;
  opacity: 0.6;
}
.submeshes .overlay-text-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.overlays .overlay-text {

}
.joint-color-sample {
  display: inline-block;
  height: 1.2em;
  width: 3em;
  border: 1px solid #333;
  vertical-align: middle;
}
.btn-bill-of-materials {
    margin-bottom: 2.5em;
}
.submesh.finished {
    opacity: 0.4;
}
</style>
